import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Activity, ExternalLink, FileText, Globe, LayoutDashboard, MessageSquare, Palette, PanelsTopLeft,
  RotateCcw, Save, Search, Users,
} from 'lucide-react';
import {
  AppButton, Callout, FormActionBar, PageHeader, StatusBadge, Tabs, TabsContent, TabsList, TabsTrigger,
} from '../ui';
import Shimmer from '../Common/Shimmer';
import CommonToaster from '../Common/CommonToaster';
import BlogsList from './BlogsList';
import TestimonialsList from './TestimonialsList';
import TeamTab from './TeamTab';
import AiUsagePanel from './AiUsagePanel';
import SeoSettingsTab from './SeoSettingsTab';
import OverviewTab from './OverviewTab';
import PagesTab from './PagesTab';
import HeaderFooterTab from './HeaderFooterTab';
import DesignTab from './DesignTab';
import useCmsWorkspace from './useCmsWorkspace';
import { getSchemaForPreset } from './pageSchemas';
import { buildSetupChecklist, layoutLabel, publicSiteUrl } from './cmsSiteModel';
import { usePlan } from '../../contexts/PlanContext';
import { getCurrentUser } from '../../utils/auth';

// ─── CMS Management — the institute's public website ─────────────────────
//
//   Overview        live or not, the address, and what is left to set up
//   Pages           the copy and images of each page
//   Header & Footer menus, logos, footer columns and social links
//   Design          layout, colours, fonts, custom code
//   Team            the mentors and trainers shown on the site
//   Blogs · Testimonials · SEO · AI usage
//
// Overview, Pages, Header & Footer and Design edit one shared draft
// (useCmsWorkspace) saved from one bar, so nothing is lost moving between
// tabs. Team, Blogs, Testimonials and SEO keep their own saves: each profile,
// post or story is its own record.

const TABS = ['overview', 'pages', 'header-footer', 'design', 'team', 'blogs', 'testimonials', 'seo', 'ai-usage'];
// Links from before these tabs existed.
const LEGACY_TABS = { settings: 'overview', layout: 'design', customization: 'design' };
const TAB_NAMES = { pages: 'Pages', 'header-footer': 'Header & Footer', design: 'Design' };

// A small dot on a tab that holds unsaved changes.
const Unsaved = ({ on }) => (on ? (
  <>
    <span className="tw-ml-1 tw-inline-block tw-h-1.5 tw-w-1.5 tw-rounded-full tw-bg-warning" aria-hidden="true" />
    <span className="tw-sr-only"> (unsaved changes)</span>
  </>
) : null);

const CmsManagement = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const requested = searchParams.get('tab') || 'overview';
  const activeTab = LEGACY_TABS[requested] || (TABS.includes(requested) ? requested : 'overview');
  const setActiveTab = useCallback((tab) => setSearchParams({ tab }, { replace: true }), [setSearchParams]);

  const { hasPlanFeature } = usePlan();
  const aiEnabled = hasPlanFeature('aiCmsContent');
  const [toast, setToast] = useState({ show: false, message: '', type: 'success' });
  const notify = useCallback((message, type = 'success') => setToast({ show: true, message, type }), []);

  const ws = useCmsWorkspace({ notify });
  const [pagesFocus, setPagesFocus] = useState(null);

  const layoutPreset = ws.settings.layoutPreset;
  const layoutName = layoutLabel(layoutPreset);
  const schema = useMemo(() => getSchemaForPreset(layoutPreset), [layoutPreset]);
  const institute = getCurrentUser()?.institute;
  const siteUrl = publicSiteUrl(institute, '/');
  const pageUrl = useCallback((path) => publicSiteUrl(institute, path), [institute]);
  const checklist = useMemo(
    () => buildSetupChecklist({ layoutPreset, content: ws.content, stats: ws.stats }),
    [layoutPreset, ws.content, ws.stats],
  );

  // The Team tab links to the heading above its people on the website. Only
  // the layouts whose schema exposes that section can edit it — theme-3 keeps
  // its copy under its own `t3_*` keys and has no About page in the Pages tab,
  // so there is nowhere to send the admin and the link is left off.
  const teamHeadingTarget = useMemo(
    () => (schema.some((p) => p.pageKey === 'about' && p.sections.some((sec) => sec.id === 'trainers'))
      ? { tab: 'pages', page: 'about', section: 'trainers' }
      : null),
    [schema],
  );
  // …and to the home page's "Trainers & mentors" section, which every layout
  // has (theme-3's lives on its own `t3_home` page).
  const teamHomeTarget = useMemo(() => {
    const home = schema.find((p) => (p.pageKey === 'home' || p.pageKey === 't3_home')
      && p.sections.some((sec) => sec.id === 'team'));
    return home ? { tab: 'pages', page: home.pageKey, section: 'team' } : null;
  }, [schema]);

  // `target` is { tab } or { tab: 'pages', page, section } — from the
  // checklist and cross-links between tabs.
  const go = useCallback((target) => {
    if (target.page) {
      setPagesFocus({ page: target.page, section: target.section, nonce: Date.now() });
      setActiveTab('pages');
    } else {
      setActiveTab(target.tab);
    }
  }, [setActiveTab]);

  // The SEO audit names pages plainly ('home'); theme-3 keeps its content
  // under prefixed keys ('t3_home'), so resolve against the active schema.
  const { applyPageSeo, isDirty, saving, save } = ws;
  const applyAuditSeo = useCallback((pageType, seo) => {
    const key = schema.find((p) => p.pageKey === pageType || p.pageKey === `t3_${pageType}`)?.pageKey || pageType;
    applyPageSeo(key, seo);
  }, [schema, applyPageSeo]);

  // Closing the tab or reloading with unsaved changes asks first.
  useEffect(() => {
    if (!isDirty) return undefined;
    const warn = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [isDirty]);

  // Ctrl/⌘ + S saves, as it does in every editor the admin already uses.
  useEffect(() => {
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 's') {
        e.preventDefault();
        if (isDirty && !saving) save();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isDirty, saving, save]);

  const errorCount = Object.keys(ws.errors).length;
  const dirtyNames = [...ws.dirtyTabs].map((t) => TAB_NAMES[t]).filter(Boolean);

  return (
    <div className={`tw-flex tw-flex-col tw-gap-5${isDirty ? ' has-form-actionbar' : ''}`}>
      <PageHeader
        icon={Globe}
        title="Website"
        description="Your public website — its pages, header and footer, design, blog and testimonials."
        actions={(
          <div className="tw-flex tw-items-center tw-gap-2">
            {!ws.loading && !ws.loadFailed && (
              <StatusBadge status={ws.settings.cmsEnabled ? 'active' : 'inactive'}>
                {ws.settings.cmsEnabled ? 'Live' : 'Off'}
              </StatusBadge>
            )}
            <AppButton as="a" size="sm" variant="outline" href={siteUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink size={16} /> Visit website
            </AppButton>
          </div>
        )}
      />

      {ws.loading && <Shimmer height={96} count={4} />}

      {!ws.loading && ws.loadFailed && (
        <Callout tone="danger" role="alert">
          <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-3">
            <span>Your website settings could not be loaded, so editing is paused — saving now could overwrite them.</span>
            <AppButton size="sm" variant="outline" onClick={ws.reload}>
              <RotateCcw size={16} /> Try again
            </AppButton>
          </div>
        </Callout>
      )}

      {!ws.loading && !ws.loadFailed && (
        <Tabs value={activeTab} onChange={setActiveTab}>
          {/* Eight tabs do not fit a 1280px admin screen beside the sidebar;
              whatever does not fit folds into a "More" menu. */}
          <TabsList overflow>
            <TabsTrigger value="overview"><LayoutDashboard size={14} /> Overview</TabsTrigger>
            <TabsTrigger value="pages"><FileText size={14} /> Pages<Unsaved on={ws.dirtyTabs.has('pages')} /></TabsTrigger>
            <TabsTrigger value="header-footer">
              <PanelsTopLeft size={14} /> Header &amp; Footer<Unsaved on={ws.dirtyTabs.has('header-footer')} />
            </TabsTrigger>
            <TabsTrigger value="design"><Palette size={14} /> Design<Unsaved on={ws.dirtyTabs.has('design')} /></TabsTrigger>
            <TabsTrigger value="team"><Users size={14} /> Team</TabsTrigger>
            <TabsTrigger value="blogs"><FileText size={14} /> Blogs</TabsTrigger>
            <TabsTrigger value="testimonials"><MessageSquare size={14} /> Testimonials</TabsTrigger>
            <TabsTrigger value="seo"><Search size={14} /> SEO &amp; Analytics</TabsTrigger>
            {aiEnabled && <TabsTrigger value="ai-usage"><Activity size={14} /> AI Usage</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview">
            <OverviewTab
              settings={ws.settings}
              settingsBusy={ws.settingsBusy}
              onToggleLive={ws.setCmsEnabled}
              siteUrl={siteUrl}
              layoutName={layoutName}
              checklist={checklist}
              stats={ws.stats}
              content={ws.content}
              onGo={go}
            />
          </TabsContent>

          <TabsContent value="pages">
            <PagesTab
              schema={schema}
              content={ws.content}
              setPageField={ws.setPageField}
              courses={ws.courses}
              aiEnabled={aiEnabled}
              notify={notify}
              focus={pagesFocus}
              pageUrl={pageUrl}
              layoutName={layoutName}
            />
          </TabsContent>

          <TabsContent value="header-footer">
            <HeaderFooterTab
              layoutPreset={layoutPreset}
              layoutName={layoutName}
              customization={ws.customization}
              setCustomizationField={ws.setCustomizationField}
              imageSlot={ws.imageSlot}
              content={ws.content}
              setPageField={ws.setPageField}
              courses={ws.courses}
              errors={ws.errors}
              onGoToPages={go}
            />
          </TabsContent>

          <TabsContent value="design">
            <DesignTab
              layoutPreset={layoutPreset}
              settingsBusy={ws.settingsBusy}
              onChangeLayout={ws.setLayoutPreset}
              brandingMode={ws.settings.layoutBrandingMode}
              setBrandingMode={ws.setBrandingMode}
              customization={ws.customization}
              setCustomizationField={ws.setCustomizationField}
              imageSlot={ws.imageSlot}
              errors={ws.errors}
            />
          </TabsContent>

          <TabsContent value="team">
            <TeamTab
              onGo={go}
              pageUrl={pageUrl}
              notify={notify}
              headingTarget={teamHeadingTarget}
              homeTarget={teamHomeTarget}
            />
          </TabsContent>

          <TabsContent value="blogs">
            <BlogsList />
          </TabsContent>

          <TabsContent value="testimonials">
            <TestimonialsList />
          </TabsContent>

          <TabsContent value="seo">
            <SeoSettingsTab onShowToast={notify} onApplyPageSeo={applyAuditSeo} />
          </TabsContent>

          {aiEnabled && (
            <TabsContent value="ai-usage">
              <AiUsagePanel />
            </TabsContent>
          )}
        </Tabs>
      )}

      {isDirty && (
        <FormActionBar
          info={errorCount
            ? `Fix ${errorCount === 1 ? '1 field' : `${errorCount} fields`} before saving.`
            : `Unsaved changes${dirtyNames.length ? ` in ${dirtyNames.join(', ')}` : ''}.`}
        >
          <AppButton variant="outline" size="sm" onClick={ws.discard} disabled={saving}>
            <RotateCcw size={16} /> Discard
          </AppButton>
          <AppButton size="sm" onClick={save} disabled={saving}>
            <Save size={16} /> {saving ? 'Saving…' : 'Save changes'}
          </AppButton>
        </FormActionBar>
      )}

      <CommonToaster
        show={toast.show}
        message={toast.message}
        type={toast.type}
        onClose={() => setToast((t) => ({ ...t, show: false }))}
      />
    </div>
  );
};

export default CmsManagement;
